<template>
  <TransitionGroup v-if="show">
    <div
      ref="expressCheckoutElementRef"
      key="express-checkout-element"
      :class="{'opacity-50 pointer-events-none cursor-not-allowed': props.disabled }"
    />

    <div
      v-if="ready"
      key="express-footer"
      class="my-4"
    >
      <div class="text-vp-base-400 text-xs uppercase">
        {{ tos?.label }}
        <button
          type="button"
          class="px-1 align-top"
          @click="onShowTosModal"
        >
          <iconify-icon
            icon="mdi:help-circle-outline"
            class="text-base"
          />
        </button>
      </div>
    </div>
  </TransitionGroup>

  <VPModal
    v-model:show="showTosModal"
    show-close-button
    hide-actions
    :title="tos?.descriptionTitle"
  >
    <div v-html="tos?.description" />
  </VPModal>
</template>

<script setup lang="ts">
import VPModal from '@/components/form/VPModal.vue';
import { onMounted, ref } from 'vue';
import { Checkout } from '@vp/common';
import * as VPError from '@/types/VPError';
import type { Stripe, StripeElements, StripeExpressCheckoutElementConfirmEvent } from '@stripe/stripe-js';
import { useToast } from 'vue-toastification';
import { useGettext } from 'vue3-gettext';

type Props = {
  tos: Checkout.CheckoutCheckboxConfig.CheckoutCheckboxConfig;
  show?: boolean;
  stripe: Stripe;
  elements: StripeElements;
  disabled?: boolean;
};

const props = defineProps<Props>();

const toast = useToast();
const { $gettext } = useGettext();

const showTosModal = ref(false);
const expressCheckoutElementRef = ref<HTMLElement | undefined>();
const ready = ref(false);

const emit = defineEmits<{
  (e: 'quick-checkout-load'): void;
  (e: 'confirm', event: StripeExpressCheckoutElementConfirmEvent): void;
}>();

const onShowTosModal = () => {
  if (!props.tos) {
    return;
  }

  showTosModal.value = true;
};

onMounted(() => {
  if (expressCheckoutElementRef.value) {
    initExpressCheckoutElement(expressCheckoutElementRef.value);
  }
});

const initExpressCheckoutElement = (element: HTMLElement): void => {
  if (!props.elements) {
    throw new Error('elements is not initialized');
  }

  const expressElement = props.elements.create('expressCheckout', {
    paymentMethodOrder: ['applePay', 'googlePay', 'paypal', 'link'],
    buttonType: {
      applePay: 'check-out',
      googlePay: 'checkout',
    },
    emailRequired: true,
    phoneNumberRequired: true,
    billingAddressRequired: true,
  });

  expressElement.mount(element);

  expressElement.on('ready', ({ availablePaymentMethods }) => {
    if (availablePaymentMethods) {
      ready.value = true;
      emit('quick-checkout-load');
    }
  });

  expressElement.on('confirm', event => {
    if (!props.stripe) {
      throw VPError.getNew('Stripe is not initialized!');
    }

    emit('confirm', event);
  });

  expressElement.on('cancel', () => {
    toast.error($gettext('Quick checkout canceled'));
  });
};
</script>
